import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { FieldRow } from "@/components/ui/Field";
import { AngebotBeauftragungPicker } from "@/features/globalUpload/AngebotBeauftragungPicker";
import { AuftragMultiPicker } from "@/features/globalUpload/AuftragMultiPicker";
import { ExtensionWarningDialog } from "@/features/globalUpload/ExtensionWarningDialog";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import {
  buildUploadEntries,
  isUploadFilenameConflict,
  parseUploadConflict,
  uploadFilesToAuftraege,
} from "@/features/globalUpload/globalUploadApi";
import { requestAuftragsbestaetigungFromAngebot } from "@/pages/dokumente/auftragsbestaetigungFromAngebotEvents";
import {
  GLOBAL_UPLOAD_NAME_TEMPLATES,
  SCHADENSMELDUNG_NAME_TEMPLATE,
  type GlobalUploadFileEntry,
} from "@/features/globalUpload/globalUploadConstants";
import {
  applyExtensionCorrections,
  applyTemplateToFileName,
  findFilesWithChangedExtension,
  hasChangedFileExtension,
} from "@/features/globalUpload/globalUploadFileName";
import { GLOBAL_UPLOAD_Z } from "@/features/globalUpload/globalUploadLayers";
import { canViewHref } from "@/lib/acl";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userIsBuero, userIsMonteure } from "@/lib/auth";
import type { BeauftragbaresAngebot } from "@/lib/dokumente";
import type { AuftragLookup } from "@/lib/material";
import { getOrder } from "@/lib/projectOverview";

function formatFileSize(bytes: number): string {
  return `${Math.round(bytes / 1024)} kB`;
}

function angebotToAuftragLookup(angebot: BeauftragbaresAngebot): AuftragLookup | null {
  if (!angebot.auftrag_id) return null;
  return {
    id: angebot.auftrag_id,
    auftragsnummer: angebot.auftrag_nummer || String(angebot.auftrag_id),
    beschreibung: angebot.auftrag_beschreibung ?? null,
    ort: angebot.auftrag_ort ?? null,
    ort_zusatz: null,
    kunde: angebot.kunde_name ?? null,
  };
}

function orderToAuftragLookup(order: {
  id: number;
  auftragsnummer: string;
  jahr?: number | null;
  beschreibung?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  kunde?: string | null;
}): AuftragLookup {
  return {
    id: order.id,
    auftragsnummer: order.auftragsnummer,
    jahr: order.jahr ?? null,
    beschreibung: order.beschreibung ?? null,
    ort: order.ort ?? null,
    ort_zusatz: order.ort_zusatz ?? null,
    kunde: order.kunde ?? null,
  };
}

function nestedDialogZIndex(zIndexClass: string): string {
  const match = zIndexClass.match(/z-\[(\d+)\]/);
  if (match) return `z-[${Number(match[1]) + 10}]`;
  return "z-[80]";
}

export function GlobalUploadDialog({
  open,
  onClose,
  files,
  onFilesChange,
  onAddFiles,
  preselectedAuftragId = null,
  zIndexClass = GLOBAL_UPLOAD_Z,
}: {
  open: boolean;
  onClose: () => void;
  files: GlobalUploadFileEntry[];
  onFilesChange: (next: GlobalUploadFileEntry[]) => void;
  onAddFiles: (fileList: FileList | File[]) => void;
  /** Wenn die Projektübersicht offen ist: dieses Projekt vorauswählen. */
  preselectedAuftragId?: number | null;
  zIndexClass?: string;
}) {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);
  const isMonteurOnly = userIsMonteure(user) && !isBuero;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedAuftraege, setSelectedAuftraege] = useState<AuftragLookup[]>([]);
  const [preselectLocked, setPreselectLocked] = useState(false);
  const [activeFileIndex, setActiveFileIndex] = useState<number | null>(null);
  const [status, setStatus] = useState<{ variant: "info" | "success" | "error"; message: string } | null>(
    null,
  );
  const [uploading, setUploading] = useState(false);
  const [extensionWarningOpen, setExtensionWarningOpen] = useState(false);
  const [dokumenteFeatureEnabled, setDokumenteFeatureEnabled] = useState(false);
  const [selectedBeauftragAngebot, setSelectedBeauftragAngebot] = useState<BeauftragbaresAngebot | null>(
    null,
  );

  const beauftragAuftrag = useMemo(
    () => (selectedBeauftragAngebot ? angebotToAuftragLookup(selectedBeauftragAngebot) : null),
    [selectedBeauftragAngebot],
  );

  const effectiveAuftraege = useMemo(
    () => (beauftragAuftrag ? [beauftragAuftrag] : selectedAuftraege),
    [beauftragAuftrag, selectedAuftraege],
  );

  const effectiveActiveIndex =
    activeFileIndex ?? (files.length > 0 ? 0 : null);

  const hasSchadensmeldungMark = files.some((entry) => entry.asSchadensmeldung);

  const resetDialogState = useCallback(() => {
    setSelectedAuftraege([]);
    setPreselectLocked(false);
    setActiveFileIndex(null);
    setStatus(null);
    setUploading(false);
    setExtensionWarningOpen(false);
    setSelectedBeauftragAngebot(null);
  }, []);

  const handleClose = useCallback(() => {
    resetDialogState();
    onClose();
  }, [onClose, resetDialogState]);

  useEffect(() => {
    if (!open || isMonteurOnly) {
      setDokumenteFeatureEnabled(false);
      return;
    }
    let cancelled = false;
    void canViewHref("/dokumente.html", user?.groups || []).then((ok) => {
      if (!cancelled) setDokumenteFeatureEnabled(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [open, user?.groups, isMonteurOnly]);

  useEffect(() => {
    if (!open || !preselectedAuftragId) {
      if (!open) setPreselectLocked(false);
      return;
    }
    let cancelled = false;
    setPreselectLocked(true);
    void getOrder(preselectedAuftragId)
      .then((order) => {
        if (cancelled || !order?.id) return;
        setSelectedAuftraege([orderToAuftragLookup(order)]);
      })
      .catch(() => {
        if (!cancelled) setPreselectLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, preselectedAuftragId]);

  function updateFile(index: number, patch: Partial<GlobalUploadFileEntry>) {
    onFilesChange(files.map((entry, idx) => (idx === index ? { ...entry, ...patch } : entry)));
  }

  function removeFile(index: number) {
    onFilesChange(files.filter((_, idx) => idx !== index));
    if (activeFileIndex === index) {
      setActiveFileIndex(null);
    } else if (activeFileIndex !== null && activeFileIndex > index) {
      setActiveFileIndex(activeFileIndex - 1);
    }
  }

  function applyTemplate(templateValue: string) {
    if (effectiveActiveIndex === null || !files[effectiveActiveIndex]) {
      setStatus({ variant: "error", message: "Bitte zuerst eine Datei auswählen." });
      return;
    }
    const entry = files[effectiveActiveIndex];
    const combinedName = applyTemplateToFileName(templateValue, entry.original.name);
    updateFile(effectiveActiveIndex, {
      customName: combinedName,
      hasConflict: false,
      asSchadensmeldung: false,
    });
  }

  function applySchadensmeldungMark() {
    if (effectiveActiveIndex === null || !files[effectiveActiveIndex]) {
      setStatus({ variant: "error", message: "Bitte zuerst eine Datei auswählen." });
      return;
    }
    const entry = files[effectiveActiveIndex];
    const combinedName = applyTemplateToFileName(
      SCHADENSMELDUNG_NAME_TEMPLATE.value,
      entry.original.name,
    );
    updateFile(effectiveActiveIndex, {
      customName: combinedName,
      hasConflict: false,
      asSchadensmeldung: true,
    });
    setSelectedBeauftragAngebot(null);
    setStatus({
      variant: "info",
      message: "Datei als Schadensmeldung / FM-Auftrag markiert.",
    });
  }

  async function performUpload(filesToUpload: GlobalUploadFileEntry[] = files) {
    const auftragIds = effectiveAuftraege.map((a) => a.id);
    const uploadEntries = buildUploadEntries(filesToUpload, auftragIds);
    setUploading(true);
    setStatus({ variant: "info", message: "Lade Dateien hoch …" });

    try {
      const beauftragFileIndex = effectiveActiveIndex ?? 0;
      const beauftragungen =
        !isMonteurOnly &&
        dokumenteFeatureEnabled &&
        selectedBeauftragAngebot?.auftrag_id &&
        auftragIds.includes(selectedBeauftragAngebot.auftrag_id)
          ? {
              [selectedBeauftragAngebot.auftrag_id]: [
                {
                  angebot_id: selectedBeauftragAngebot.id,
                  file_index: beauftragFileIndex,
                },
              ],
            }
          : undefined;

      const schadensmeldungen: Record<number, { file_index: number }[]> = {};
      for (const auftragId of auftragIds) {
        const targets = filesToUpload
          .map((entry, fileIndex) => ({ entry, fileIndex }))
          .filter(({ entry }) => entry.asSchadensmeldung)
          .map(({ fileIndex }) => ({ file_index: fileIndex }));
        if (targets.length) {
          schadensmeldungen[auftragId] = targets;
        }
      }

      await uploadFilesToAuftraege(
        uploadEntries,
        beauftragungen,
        Object.keys(schadensmeldungen).length ? schadensmeldungen : undefined,
      );
      const commissionedAngebotId = selectedBeauftragAngebot?.id ?? null;
      setStatus({ variant: "success", message: "Upload abgeschlossen." });
      onFilesChange([]);
      window.dispatchEvent(new Event("auftraege_changed"));
      window.dispatchEvent(new Event("dokumente_changed"));
      window.dispatchEvent(new Event("timesheets_changed"));
      if (Object.keys(schadensmeldungen).length) {
        window.dispatchEvent(new Event("montageunterlagen_changed"));
      }
      window.setTimeout(() => {
        handleClose();
        if (commissionedAngebotId) {
          requestAuftragsbestaetigungFromAngebot(commissionedAngebotId);
        }
      }, 800);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        if (isUploadFilenameConflict(err)) {
          const { message, conflictFileIndex } = parseUploadConflict(err, uploadEntries, filesToUpload);
          if (conflictFileIndex !== null) {
            onFilesChange(
              filesToUpload.map((entry, idx) => ({
                ...entry,
                hasConflict: idx === conflictFileIndex,
              })),
            );
            setActiveFileIndex(conflictFileIndex);
          }
          setStatus({ variant: "error", message });
        } else {
          setStatus({ variant: "error", message: err.message });
        }
        return;
      }
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Upload fehlgeschlagen.";
      setStatus({ variant: "error", message: `Upload fehlgeschlagen: ${message}` });
    } finally {
      setUploading(false);
    }
  }

  function handleUpload() {
    const auftragIds = effectiveAuftraege.map((a) => a.id);
    if (!auftragIds.length) {
      setStatus({
        variant: "error",
        message:
          !isMonteurOnly && dokumenteFeatureEnabled
            ? "Bitte ein Projekt oder ein Angebot auswählen."
            : "Bitte mindestens ein Projekt auswählen.",
      });
      return;
    }
    if (!files.length) {
      setStatus({ variant: "error", message: "Bitte mindestens eine Datei auswählen." });
      return;
    }
    if (isMonteurOnly && !hasSchadensmeldungMark) {
      setStatus({
        variant: "error",
        message: "Bitte die Datei als Schadensmeldung / FM-Auftrag markieren.",
      });
      return;
    }
    if (isMonteurOnly && !files.every((entry) => entry.asSchadensmeldung)) {
      setStatus({
        variant: "error",
        message: "Monteure können nur als Schadensmeldung / FM-Auftrag hochladen. Bitte alle Dateien markieren.",
      });
      return;
    }

    if (findFilesWithChangedExtension(files).length > 0) {
      setExtensionWarningOpen(true);
      return;
    }

    void performUpload();
  }

  function handleConfirmExtensionWarning() {
    setExtensionWarningOpen(false);
    void performUpload();
  }

  function handleApplyCorrectionsAndUpload() {
    const corrected = applyExtensionCorrections(files);
    onFilesChange(corrected);
    setExtensionWarningOpen(false);
    void performUpload(corrected);
  }

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      dirty={!uploading && (files.length > 0 || effectiveAuftraege.length > 0)}
      title="Dateien zu Projekten hochladen"
      size="lg"
      zIndexClass={zIndexClass}
      footer={
        <>
          <DialogCancelButton onClose={handleClose} disabled={uploading} />
          <Button type="button" onClick={handleUpload} loading={uploading}>
            Dateien hochladen
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {isMonteurOnly ? (
          <Alert variant="info">
            Als Monteur kannst du Dateien nur als Schadensmeldung / FM-Auftrag hochladen. Markiere
            die Datei über den Button und wähle ein Projekt.
          </Alert>
        ) : null}

        <FieldRow label="Ausgewählte Dateien">
          {files.length === 0 ? (
            <p className="text-sm text-label-secondary">Noch keine Dateien ausgewählt.</p>
          ) : (
            <ul className="space-y-2">
              {files.map((entry, idx) => (
                <li
                  key={entry.id}
                  className={clsx(
                    "rounded-lg border px-3 py-2",
                    entry.hasConflict
                      ? "border-status-orange bg-[color-mix(in_srgb,_var(--eh-orange)_12%,_var(--eh-bg-card))]"
                      : entry.asSchadensmeldung
                        ? "border-accent bg-accent-soft/30"
                        : "border-separator bg-card-elevated",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm text-label">
                      {entry.original.name} ({formatFileSize(entry.original.size)})
                    </span>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeFile(idx)}
                      disabled={uploading}
                    >
                      Entfernen
                    </Button>
                  </div>
                  {entry.asSchadensmeldung ? (
                    <p className="mt-1 text-xs font-medium text-accent">
                      Schadensmeldung / FM-Auftrag
                    </p>
                  ) : null}
                  {!isMonteurOnly ? (
                    <input
                      type="text"
                      className={clsx(
                        "eh-input mt-2 text-sm",
                        effectiveActiveIndex === idx && "ring-2 ring-accent-soft",
                        entry.hasConflict && "border-status-orange",
                        hasChangedFileExtension(entry.original.name, entry.customName) &&
                          "border-status-orange",
                      )}
                      placeholder="{{Projektnr.}}-{{Dokumentenname}}.pdf"
                      value={entry.customName}
                      onFocus={() => setActiveFileIndex(idx)}
                      onChange={(e) =>
                        updateFile(idx, { customName: e.target.value, hasConflict: false })
                      }
                      disabled={uploading}
                    />
                  ) : (
                    <button
                      type="button"
                      className={clsx(
                        "mt-2 w-full rounded-lg border px-3 py-2 text-left text-sm",
                        effectiveActiveIndex === idx
                          ? "border-accent bg-card"
                          : "border-separator bg-card-elevated",
                      )}
                      onClick={() => setActiveFileIndex(idx)}
                    >
                      {entry.customName || entry.original.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              Weitere Dateien auswählen …
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) {
                  onAddFiles(e.target.files);
                  setStatus(null);
                  e.target.value = "";
                }
              }}
            />
          </div>
        </FieldRow>

        {!isMonteurOnly ? (
          <FieldRow
            label="Dateinamen-Vorschläge"
            hint="Wähle zuerst eine Datei aus und klicke dann auf einen Vorschlag."
          >
            <div className="flex flex-wrap gap-2">
              {GLOBAL_UPLOAD_NAME_TEMPLATES.map((template) => (
                <Button
                  key={template.label}
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => applyTemplate(template.value)}
                  disabled={uploading}
                >
                  {template.label}
                </Button>
              ))}
            </div>
          </FieldRow>
        ) : null}

        <FieldRow
          label="Schadensmeldung / FM-Auftrag"
          hint={
            isMonteurOnly
              ? "Datei auswählen und markieren. Das Projekt wird wie bei einer Beauftragung geführt; Monteure können die Datei einsehen."
              : "Markiert die aktive Datei als Schadensmeldung / FM-Auftrag (wie Beauftragung, für Monteure einsehbar)."
          }
        >
          <Button
            type="button"
            variant={hasSchadensmeldungMark ? "primary" : "secondary"}
            size="sm"
            onClick={applySchadensmeldungMark}
            disabled={uploading}
          >
            {SCHADENSMELDUNG_NAME_TEMPLATE.label}
          </Button>
        </FieldRow>

        <hr className="border-separator" />

        {!isMonteurOnly && dokumenteFeatureEnabled ? (
          <AngebotBeauftragungPicker
            value={selectedBeauftragAngebot}
            onChange={(next) => {
              setSelectedBeauftragAngebot(next);
              if (next) {
                onFilesChange(files.map((entry) => ({ ...entry, asSchadensmeldung: false })));
              }
            }}
            disabled={uploading}
          />
        ) : null}

        {beauftragAuftrag ? (
          <FieldRow
            label="Zielprojekt"
            hint="Wird automatisch aus dem gewählten Angebot übernommen."
          >
            <div className="rounded-lg border border-separator bg-card-elevated px-3 py-2 text-sm text-label">
              {formatAuftragLookupLabel(beauftragAuftrag)}
            </div>
          </FieldRow>
        ) : preselectLocked && selectedAuftraege.length === 1 ? (
          <FieldRow
            label="Zielprojekt"
            hint="Aus der geöffneten Projektübersicht vorausgewählt."
          >
            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg border border-separator bg-card-elevated px-3 py-2 text-sm text-label">
                {formatAuftragLookupLabel(selectedAuftraege[0]!)}
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPreselectLocked(false)}
                disabled={uploading}
              >
                Anderes Projekt wählen
              </Button>
            </div>
          </FieldRow>
        ) : (
          <AuftragMultiPicker
            key={open ? `open-${preselectedAuftragId ?? "none"}` : "closed"}
            value={selectedAuftraege}
            onChange={setSelectedAuftraege}
            singleSelect={isMonteurOnly}
          />
        )}

        {status ? (
          <Alert
            variant={status.variant === "error" ? "error" : status.variant === "success" ? "success" : "info"}
          >
            {status.message}
          </Alert>
        ) : null}
      </div>
    </Dialog>
    <ExtensionWarningDialog
      open={extensionWarningOpen}
      files={findFilesWithChangedExtension(files)}
      onApplyCorrectionsAndUpload={handleApplyCorrectionsAndUpload}
      onConfirm={handleConfirmExtensionWarning}
      onCancel={() => setExtensionWarningOpen(false)}
      loading={uploading}
      zIndexClass={nestedDialogZIndex(zIndexClass)}
    />
    </>
  );
}
